import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../../commons/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Styles from "./LegerViewType.module.css";
import { useDispatch, useSelector } from "react-redux";
import { formatDate, formatSlotTime } from "../../../helpers/dateFormat";
import { Switch, styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { USER_TYPES } from "../LedgerPageDashboardActive/LedgerPageDashboardActive";
import { openBilateralChat } from "../../Modals/Banking/CreateEdit/helpers";

export const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "var(--primary-color-text)",
        ...theme.applyStyles("dark", {
          backgroundColor: "var(--primary-color-text)",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(149, 148, 148, 0.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

const LegerViewType = ({
  data,
  companyUpdate,
  requisition,
  handleSelectedBids,
  handleReq,
  hideBucketFill,
  setHideBucketFill,
}) => {
  const onGoingData = useSelector(
    (state) => state?.CapacitySlice?.activeConnection
  );

  const dispatch = useDispatch();
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole
  )?.toLowerCase();
  const [expanded, setExpanded] = useState(0);
  const handleAccordionChange = (val) => {
    if (val === expanded) {
      setExpanded("");
    } else {
      setExpanded(val);
    }
  };

  useEffect(() => {
    setHideBucketFill(false);
  }, [setHideBucketFill]);

  function filterMatchSlotsByFinalizedConnection() {
    // Determine which key to use based on user type
    const outerKey = user === "consumer" ? "generators" : "consumers";
    const requiredInitiator = user === "consumer" ? "generator" : "consumer";

    return data?.map((item) => {
      // Filter and process counterparties
      const updatedCounterparties = (item[outerKey] || [])
        .map((counterparty) => {
          if (counterparty.initiatorType === requiredInitiator) {
            // Filter matchSlots to keep only those with finalizedConnectionValues
            const filteredMatchSlots = (counterparty.matchSlots || []).filter(
              (slot) => !!slot.finalizedConnectionValues
            );

            return {
              ...counterparty,
              matchSlots: filteredMatchSlots,
            };
          }
          // Otherwise, leave matchSlots unchanged
          return counterparty;
        })
        .filter((counterparty) => {
          // Remove counterparty if initiatorType is opposite and no matchSlots have finalizedConnectionValues
          if (counterparty.initiatorType === requiredInitiator) {
            // If after filtering, matchSlots is empty, remove this counterparty
            return counterparty.matchSlots.length > 0;
          }
          // Keep counterparties with different initiatorType
          return true;
        });

      return {
        ...item,
        [outerKey]: updatedCounterparties,
      };
    });
  }

  const filteredData = filterMatchSlotsByFinalizedConnection();

  const handleUserSelectBid = ({
    index,
    counterData,
    counterDetails,
    selfData,
  }) => {
    let self_Data = selfData;
    let counter_Data = counterData;

    // Create a unique identifier combining requisition and match slot
    const uniqueId = `${selfData?.id}-${counterData?.id}`;

    const exists = requisition?.some((ele) => ele?.uniqueId === uniqueId);

    if (exists) {
      // Remove the existing one
      const updatedRequisition = requisition?.filter(
        (ele) => ele?.uniqueId !== uniqueId
      );
      handleReq(updatedRequisition);

      // Remove from selectedBids
      handleSelectedBids({
        ...counterData,
        company: counterDetails?.companyName,
        consumerGeneratorBidId: counterDetails?.bidId,
        selected: false,
        uniqueId,
      });
    } else {
      // Add a new one
      const reqPayload = {
        compId: counterDetails?.companyId,
        id: counter_Data?.id,
        uniqueId: uniqueId, // Add unique identifier
        self: self_Data,
        counter: counter_Data,
      };

      const updatedRequisition = [...(requisition || []), reqPayload];
      handleReq(updatedRequisition);

      // Add to selectedBids
      handleSelectedBids({
        ...counterData,
        company: counterDetails?.companyName,
        consumerGeneratorBidId: counterDetails?.bidId,
        selected: true,
        selfRequisitionIndex: index,
        uniqueId,
      });
    }
  };

  // Function to handle company-level toggle
  const handleCompanyToggle = (counter, selfData, accordionIndex) => {
    const companyMatchSlots = counter?.matchSlots || [];

    // Check if all slots for this company in this accordion are selected
    const allSelected = companyMatchSlots.every((slot) =>
      requisition?.some(
        (req) => req?.uniqueId === `${selfData?.id}-${slot?.id}`
      )
    );

    if (allSelected) {
      // Deselect all slots for this company in this accordion
      const slotsToDeselect = [];
      const updatedRequisition = requisition?.filter((ele) => {
        const shouldRemove = companyMatchSlots.some(
          (slot) => ele?.uniqueId === `${selfData?.id}-${slot?.id}`
        );
        if (shouldRemove) {
          const slot = companyMatchSlots.find(
            (s) => ele?.uniqueId === `${selfData?.id}-${s?.id}`
          );
          if (slot) {
            slotsToDeselect.push({
              ...slot,
              company: counter?.companyName,
              consumerGeneratorBidId: counter?.bidId,
              selected: false,
              uniqueId: ele?.uniqueId,
            });
          }
        }
        return !shouldRemove;
      });

      // Update requisition state once
      handleReq(updatedRequisition);

      // Update selectedBids for all deselected slots
      slotsToDeselect.forEach((slot) => {
        handleSelectedBids(slot);
      });
    } else {
      // Select all slots for this company in this accordion
      const newRequisitions = [];
      const selectedBidsToUpdate = [];

      companyMatchSlots.forEach((slot, slotIndex) => {
        const uniqueId = `${selfData?.id}-${slot?.id}`;
        const exists = requisition?.some((ele) => ele?.uniqueId === uniqueId);

        if (!exists) {
          // Add new requisition for slots that don't exist
          const reqPayload = {
            compId: counter?.companyId,
            id: slot?.id,
            uniqueId: uniqueId,
            self: selfData,
            counter: slot,
          };

          newRequisitions.push(reqPayload);
        }

        // Always add to selectedBids (both existing and new slots)
        selectedBidsToUpdate.push({
          ...slot,
          company: counter?.companyName,
          consumerGeneratorBidId: counter?.bidId,
          selected: true,
          selfRequisitionIndex: `${accordionIndex}-${slotIndex}`,
          uniqueId,
        });
      });

      // Update requisition state with new requisitions if any
      if (newRequisitions.length > 0) {
        const updatedRequisition = [...(requisition || []), ...newRequisitions];
        handleReq(updatedRequisition);
      }

      // Update selectedBids for all slots (both existing and new)
      selectedBidsToUpdate.forEach((bid) => {
        handleSelectedBids(bid);
      });
    }
  };

  const getRoleDataKey = (user) =>
    user === USER_TYPES.CONSUMER ? "capacityConsumer" : "capacityGenerator";
  const roleDataKey = getRoleDataKey(user);

  const initiator = onGoingData?.[roleDataKey]?.initiatorType;

  // Function to check if all slots for a company in specific accordion are selected
  const isCompanyFullySelected = (counter, selfData) => {
    const companyMatchSlots = counter?.matchSlots || [];

    if (companyMatchSlots.length === 0) return false;

    return companyMatchSlots.every((slot) =>
      requisition?.some(
        (req) => req?.uniqueId === `${selfData?.id}-${slot?.id}`
      )
    );
  };

  // Add a state to track initialization
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Add this useEffect to pre-populate requisition and selectedBids with connectionId items
  React.useEffect(() => {
    // Only run if we have filteredData and haven't initialized yet
    if (!filteredData || filteredData.length === 0 || isInitialized) return;

    const initialRequisitions = [];
    const initialSelectedBids = [];
    let hasConnectionIds = false;

    filteredData.forEach((ele, accordionIndex) => {
      const selfData = ele?.selfSlot;
      const counterData =
        user === "consumer" ? ele?.generators : ele?.consumers;

      counterData?.forEach((counter) => {
        counter?.matchSlots?.forEach((elem, slotIndex) => {
          // Check if this item has connectionId or finalizedConnectionValues
          const hasConnectionId =
            elem?.connectionId || elem?.finalizedConnectionValues?.id;

          if (hasConnectionId) {
            hasConnectionIds = true;
            const uniqueId = `${selfData?.id}-${elem?.id}`;

            // Add to initial requisitions
            const reqPayload = {
              compId: counter?.companyId,
              id: elem?.id,
              uniqueId: uniqueId,
              self: selfData,
              counter: elem,
            };
            initialRequisitions.push(reqPayload);

            // Add to initial selected bids
            const bidPayload = {
              ...elem,
              company: counter?.companyName,
              consumerGeneratorBidId: counter?.bidId,
              selected: true,
              selfRequisitionIndex: `${accordionIndex}-${slotIndex}`,
              uniqueId,
            };
            initialSelectedBids.push(bidPayload);
          }
        });
      });
    });

    // Only update if there are items with connectionId
    if (hasConnectionIds && initialRequisitions.length > 0) {
      // Update requisition state
      handleReq(initialRequisitions);

      // Update selectedBids state for all pre-selected items
      initialSelectedBids.forEach((bid) => {
        handleSelectedBids(bid);
      });

      // Mark as initialized
      setIsInitialized(true);
    }
  }, [filteredData, handleReq, handleSelectedBids, isInitialized, user]); // Remove handleReq and handleSelectedBids from dependencies

  // Reset initialization when filteredData changes
  React.useEffect(() => {
    setIsInitialized(false);
  }, [filteredData?.length]);

  const handleOpenChat = (fromName, toName) => {
    let chatId;

    if (user === "CONSUMER") {
      chatId = `${fromName?.split("_")?.[0]}_${toName?.split("_")?.[0]}`;
    } else {
      chatId = `${toName?.split("_")?.[0]}_${fromName?.split("_")?.[0]}`;
    }
    openBilateralChat(dispatch, {
      header: fromName,
      fromUser: fromName,
      toUser: toName,
      chatId: chatId,
    });
  };

  return (
    <div className={Styles.accordionSummaryContainer}>
      {filteredData?.map((ele, index) => {
        if (!ele["consumers"]?.length && !ele["generators"]?.length)
          return null;
        let selfData = ele?.selfSlot;
        let counterData =
          user === "consumer" ? ele?.generators : ele?.consumers;

        // Calculate total requisition quantity
        const totalReqQuantity =
          Number(selfData?.purchaseEnergy || selfData?.sellEnergy) || 0;

        // Calculate selected quantity for this requisition
        let selectedQuantity = 0;
        if (!onGoingData?.length && initiator === user) {
          selectedQuantity = counterData?.reduce((sum, counter) => {
            return (
              sum +
              (counter?.matchSlots?.reduce((slotSum, elem) => {
                return (
                  slotSum +
                  (Number(elem?.purchaseEnergy || elem?.sellEnergy) || 0)
                );
              }, 0) || 0)
            );
          }, 0);
        } else {
          selectedQuantity = counterData?.reduce((sum, counter) => {
            return (
              sum +
              (counter?.matchSlots?.reduce((slotSum, elem) => {
                const isSelected = requisition?.some(
                  (req) => req?.uniqueId === `${selfData?.id}-${elem?.id}`
                );
                return (
                  slotSum +
                  (isSelected
                    ? Number(elem?.purchaseEnergy || elem?.sellEnergy) || 0
                    : 0)
                );
              }, 0) || 0)
            );
          }, 0);
        }

        // Calculate bucket fill percentage
        const bucketFillPercent =
          totalReqQuantity > 0
            ? Math.round((selectedQuantity / totalReqQuantity) * 100)
            : 0;

        return (
          <div
            className={
              expanded === index
                ? Styles.detailsMainDrop
                : Styles.detailsMainDrop_inactive
            }
          >
            <Accordion
              style={{
                marginBottom: "5px",

                border:
                  expanded === index
                    ? "1px solid var(--primary-color-text)"
                    : "none",
              }}
              expanded={expanded === index}
              onChange={() => handleAccordionChange(index)}
            >
              <AccordionSummary
                style={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  borderBottom: "var(--border-level2)",

                  borderRadiusBottom: 0,
                }}
              >
                <table className={Styles.ledgerTableHeading}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                          marginTop: "10px",
                          paddingLeft: "38px",
                          width: user === "consumer" ? "16.6%" : "20%",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <ExpandMoreIcon
                          style={{ fontSize: "18px" }}
                          className={`${Styles.expandIconLeft} ${
                            expanded === index ? Styles.rotateIcon : ""
                          }`}
                        />
                        {index + 1}
                      </th>
                      <th
                        style={{ width: user === "consumer" ? "16.6%" : "20%" }}
                        className=""
                      >
                        {selfData?.purchaseEnergy || selfData?.sellEnergy} (MW)
                      </th>
                      <th
                        style={{ width: user === "consumer" ? "16.6%" : "20%" }}
                        className=""
                      >
                        {selfData?.energyRateStr ||
                          selfData?.landingCost ||
                          selfData?.sellingCost}{" "}
                        (₹/KWh)
                      </th>
                      <th
                        style={{ width: user === "consumer" ? "16.6%" : "20%" }}
                        className=""
                      >
                        {formatDate(selfData?.dateStartAt)} {" - "}
                        {formatDate(selfData?.dateEndAt)}
                      </th>
                      <th
                        style={{
                          width: user === "consumer" ? "16.6%" : "20%",
                          textAlign: user === "consumer" ? "right" : "center",
                        }}
                        className=""
                      >
                        {formatSlotTime(selfData?.slotStartAt)}-
                        {formatSlotTime(selfData?.slotEndAt)}
                      </th>
                      {user === "consumer" && (
                        <th
                          className=""
                          style={{
                            textAlign: "right",
                            width: user === "consumer" ? "16.6%" : "20%",
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              left: "-56px",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <CircularProgress
                              variant="determinate"
                              value={
                                bucketFillPercent > 100
                                  ? 100
                                  : bucketFillPercent
                              }
                              size={40}
                              thickness={4}
                              style={{
                                position: "absolute",
                                right: "-5px",
                                top: "-20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "end",
                                color: "#00d26a",
                                background: "transparent",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "end",
                                fontWeight: 500,
                                fontSize: "12px",
                                color: "var(--sixth-color-text)",
                              }}
                            >
                              {bucketFillPercent}%
                            </div>
                          </div>
                        </th>
                      )}
                    </tr>
                  </thead>
                </table>
              </AccordionSummary>
              <AccordionDetails style={{ backgroundColor: "#fbfbfd" }}>
                <>
                  {counterData?.map((counter, i) => {
                    return (
                      <div className={Styles.accordionDetailsContainer}>
                        {i === 0 && (
                          <div className={Styles.detailsHeaderText}>
                            Counterparty Requisition
                          </div>
                        )}
                        <div
                          style={{
                            backgroundColor:
                              (user === "consumer" &&
                                counter?.initiatorType === "generator") ||
                              (user === "generator" &&
                                counter?.initiatorType === "consumer")
                                ? "#FEBC2F14"
                                : "",
                            paddingRight:
                              user === USER_TYPES.CONSUMER ? "45px" : "35px",
                          }}
                          className={`display_flex_row ${Styles.accHeaderTable_name}`}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div>
                              {counter?.companyName}
                              <div
                                style={{
                                  fontSize: "10px",
                                  marginTop: "4px",
                                  color: "#8C8A96",
                                }}
                              >
                                {counter?.name}
                              </div>
                            </div>
                            {((user === "consumer" &&
                              counter?.initiatorType === "generator") ||
                              (user === "generator" &&
                                counter?.initiatorType === "consumer")) && (
                              <div
                                style={{
                                  color: "var(--primary-color-text)",
                                  marginLeft: "40px",
                                  fontWeight: 500,
                                  fontSize: "10px",
                                }}
                              >
                                T&C Request received
                              </div>
                            )}
                          </div>

                          <div>
                            <div className={Styles.chatButtonContainer}>
                              <button
                                type="button"
                                className={Styles.chatButton}
                                onClick={() =>
                                  handleOpenChat(
                                    onGoingData[0][roleDataKey]?.name,
                                    counter?.name
                                  )
                                }
                              >
                                Chat
                              </button>
                            </div>
                            {onGoingData?.length && (
                              <div style={{ marginLeft: "30px" }}>
                                <AntSwitch
                                  checked={isCompanyFullySelected(
                                    counter,
                                    selfData
                                  )}
                                  onChange={() =>
                                    handleCompanyToggle(
                                      counter,
                                      selfData,
                                      index
                                    )
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        {counter?.matchSlots?.map((elem, i) => {
                          return (
                            <table className={Styles.ledgerTableHeading}>
                              <thead>
                                <tr
                                  style={{
                                    height: "40px",
                                    position: "relative",
                                  }}
                                >
                                  <th
                                    style={{
                                      textAlign: "left",
                                      paddingLeft: "25px",
                                      width:
                                        user === "consumer" ? "16.6%" : "20%",
                                      color: "#8C8A96",
                                    }}
                                    className=""
                                  >
                                    Requisition {i + 1}
                                  </th>
                                  <th
                                    className=""
                                    style={{
                                      width:
                                        user === USER_TYPES.CONSUMER
                                          ? "16.6%"
                                          : "20%",
                                    }}
                                  >
                                    {elem?.finalizedConnectionValues?.quantity
                                      ? elem?.finalizedConnectionValues
                                          ?.quantity
                                      : elem?.purchaseEnergy ||
                                        elem?.sellEnergy}{" "}
                                    (MW)
                                  </th>
                                  <th
                                    className=""
                                    style={{
                                      width:
                                        user === USER_TYPES.CONSUMER
                                          ? "16.6%"
                                          : "20%",
                                    }}
                                  >
                                    {elem?.finalizedConnectionValues?.amount
                                      ? elem?.finalizedConnectionValues?.amount
                                      : elem?.energyRateStr ||
                                        elem?.landingCost ||
                                        elem?.sellingCost}{" "}
                                    (₹/KWh)
                                  </th>
                                  <th
                                    className=""
                                    style={{
                                      width:
                                        user === USER_TYPES.CONSUMER
                                          ? "16.6%"
                                          : "20%",
                                    }}
                                  >
                                    {formatDate(elem?.dateStartAt)} {" - "}
                                    {formatDate(elem?.dateEndAt)}
                                  </th>
                                  <th
                                    className=""
                                    style={{
                                      textAlign:
                                        user === USER_TYPES.CONSUMER
                                          ? "right"
                                          : "center",
                                      width:
                                        user === USER_TYPES.CONSUMER
                                          ? "16.6%"
                                          : "20%",
                                    }}
                                  >
                                    {formatSlotTime(elem?.slotStartAt)}-
                                    {formatSlotTime(elem?.slotEndAt)}
                                  </th>
                                  <th
                                    className=""
                                    style={{
                                      position:
                                        user === USER_TYPES.CONSUMER
                                          ? "reltive"
                                          : "relative",
                                      bottom: "1px",
                                      width:
                                        user === USER_TYPES.CONSUMER
                                          ? "16.6%"
                                          : "0%",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "13px",
                                        left:
                                          user === USER_TYPES.CONSUMER
                                            ? "-43px"
                                            : "-34px",
                                        display: "flex",
                                        width: "100%",
                                        justifyContent: "end",
                                      }}
                                    >
                                      {initiator !== user ? (
                                        <AntSwitch
                                          checked={
                                            requisition?.filter(
                                              (ele) =>
                                                ele?.uniqueId ===
                                                `${selfData?.id}-${elem?.id}`
                                            )?.length > 0
                                          }
                                          onChange={() =>
                                            handleUserSelectBid({
                                              index: `${index}-${i}`,
                                              counterData: elem,
                                              counterDetails: counter,
                                              selfData: selfData,
                                            })
                                          }
                                        />
                                      ) : (
                                        <span
                                          style={{
                                            color:
                                              elem?.finalizedConnectionValues?.status?.toLowerCase() ===
                                              "pending"
                                                ? "#e5c420ff"
                                                : "inherit",
                                            fontWeight: 500,
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {elem?.finalizedConnectionValues
                                            ?.status
                                            ? elem.finalizedConnectionValues.status
                                                .charAt(0)
                                                .toUpperCase() +
                                              elem.finalizedConnectionValues.status
                                                .slice(1)
                                                .toLowerCase()
                                            : ""}
                                        </span>
                                      )}
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                            </table>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              </AccordionDetails>
            </Accordion>
          </div>
        );
      })}
    </div>
  );
};

export default LegerViewType;
